import type {
  Clock,
  FileDownload,
  IdGenerator,
  NetworkStatus,
  Platform,
  RandomSource,
  UpdateManager,
  UpdateState,
} from "../../application/ports/platform.ts";
import { createObservableStore } from "../../application/observable.ts";

export class BrowserClock implements Clock {
  now(): string {
    return new Date().toISOString();
  }

  todayGreeting(): "morning" | "afternoon" | "evening" {
    const hour = new Date().getHours();
    if (hour < 12) {
      return "morning";
    }
    if (hour < 17) {
      return "afternoon";
    }
    return "evening";
  }
}

export class BrowserIds implements IdGenerator {
  create(): string {
    return crypto.randomUUID();
  }
}

export class BrowserNetwork implements NetworkStatus {
  isOnline(): boolean {
    return navigator.onLine;
  }

  subscribe(listener: (online: boolean) => void): () => void {
    const on = () => listener(true);
    const off = () => listener(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }
}

export class BrowserDownload implements FileDownload {
  save(filename: string, contents: string, mimeType: string): void {
    const blob = new Blob([contents], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}

export class BrowserRandom implements RandomSource {
  next(): number {
    return Math.random();
  }
}

const CONTROL_CACHE = "vocably-control";

export class ServiceWorkerUpdateManager implements UpdateManager {
  private readonly store = createObservableStore<UpdateState>({ status: "idle" });

  getState = this.store.getSnapshot.bind(this.store);

  subscribe = (listener: (state: UpdateState) => void): (() => void) =>
    this.store.subscribe(() => listener(this.store.getSnapshot()));

  constructor() {
    if (!("serviceWorker" in navigator) || !import.meta.env.PROD) {
      this.store.setState({ status: "ready" });
      return;
    }
    void this.watch();
  }

  async getAcceptedBuildId(): Promise<string | null> {
    try {
      const cache = await caches.open(CONTROL_CACHE);
      const response =
        (await cache.match(new URL("accepted-build-id", document.baseURI))) ??
        (await cache.match("/accepted-build-id"));
      return response ? response.text() : null;
    } catch {
      return null;
    }
  }

  async accept(): Promise<void> {
    this.store.setState({ status: "installing" });
    const registration = await navigator.serviceWorker.getRegistration();
    const worker = registration?.waiting ?? registration?.active;
    if (!worker) {
      this.store.setState({
        status: "failed",
        message: "No service worker is available to update.",
      });
      return;
    }
    worker.postMessage({ type: "ACCEPT_UPDATE" });
    window.setTimeout(() => {
      if (this.store.getSnapshot().status === "installing") {
        window.location.reload();
      }
    }, 1500);
  }

  private requestStatus(
    worker: ServiceWorker,
  ): Promise<{ type?: string; buildId?: string; acceptedBuildId?: string | null }> {
    return new Promise((resolve) => {
      const channel = new MessageChannel();
      const timer = window.setTimeout(() => resolve({}), 2000);
      channel.port1.onmessage = (
        event: MessageEvent<{ type?: string; buildId?: string; acceptedBuildId?: string | null }>,
      ) => {
        window.clearTimeout(timer);
        resolve(event.data ?? {});
      };
      worker.postMessage({ type: "GET_STATUS" }, [channel.port2]);
    });
  }

  private markAvailable(waitingBuildId: string): void {
    if (this.store.getSnapshot().status === "installing") {
      return;
    }
    this.store.setState({ status: "available", waitingBuildId });
  }

  private markReady(): void {
    const current = this.store.getSnapshot().status;
    if (current === "installing" || current === "available") {
      return;
    }
    this.store.setState({ status: "ready" });
  }

  private applyWorkerStatus(
    registration: ServiceWorkerRegistration,
    status: { buildId?: string; acceptedBuildId?: string | null },
  ): void {
    const waiting = Boolean(registration.waiting);
    const mismatch = Boolean(status.buildId && status.acceptedBuildId && status.buildId !== status.acceptedBuildId);
    if (waiting || mismatch) {
      this.markAvailable(status.buildId ?? __APP_BUILD_ID__);
      return;
    }
    if (registration.active) {
      this.markReady();
    }
  }

  private async watch(): Promise<void> {
    try {
      this.store.setState({ status: "caching" });
      const registration = await navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`, {
        type: "module",
      });

      navigator.serviceWorker.addEventListener(
        "message",
        (event: MessageEvent<{ type?: string; acceptedBuildId?: string | null; buildId?: string }>) => {
          if (event.data?.type === "UPDATE_AVAILABLE" || event.data?.type === "STATUS") {
            this.applyWorkerStatus(registration, event.data);
          }
        },
      );

      const refreshStatus = async () => {
        const worker = registration.waiting ?? registration.active;
        if (!worker) {
          return;
        }
        const status = await this.requestStatus(worker);
        this.applyWorkerStatus(registration, status);
      };

      const watchInstalling = (worker: ServiceWorker | null) => {
        worker?.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            this.markAvailable(__APP_BUILD_ID__);
          }
          if (worker.state === "activated") {
            void refreshStatus();
          }
        });
      };
      watchInstalling(registration.installing);
      registration.addEventListener("updatefound", () => watchInstalling(registration.installing));

      await navigator.serviceWorker.ready;
      await refreshStatus();
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (this.store.getSnapshot().status === "installing") {
          window.location.reload();
        }
      });
      if (navigator.onLine) {
        void registration.update();
      }
    } catch (error) {
      this.store.setState({
        status: "failed",
        message: error instanceof Error ? error.message : "The offline app could not be installed.",
      });
    }
  }
}

export function createBrowserPlatform(): Platform {
  return {
    clock: new BrowserClock(),
    ids: new BrowserIds(),
    network: new BrowserNetwork(),
    download: new BrowserDownload(),
    random: new BrowserRandom(),
    updates: new ServiceWorkerUpdateManager(),
  };
}
